import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/layout/Navigation';
import {
  Settings,
  Lock,
  Bell,
  CreditCard,
  LogOut,
  ChevronRight,
  Shield,
  Mail,
  Phone,
  Building2,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  BadgeCheck,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { isIOS } from '../services/platform.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Plan feature map
const PLAN_FEATURES: Record<string, string[]> = {
  BASIC: ['profile', 'map', 'contact', 'badge'],
  PRO: ['profile', 'map', 'contact', 'badge', 'bookings', 'posts', 'stats', 'priority_map'],
  PREMIUM: ['profile', 'map', 'contact', 'badge', 'bookings', 'posts', 'stats', 'priority_map', 'ai_targeting', 'banner', 'push', 'crm', 'analytics'],
};

function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = {
    BASIC: 'bg-gray-100 text-gray-700 border-gray-300',
    PRO: 'bg-teal-100 text-teal-700 border-teal-300',
    PREMIUM: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${colors[plan] || colors.BASIC}`}>
      <BadgeCheck className="w-3 h-3" />
      {plan}
    </span>
  );
}

export default function SpecialistSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Notification preferences
  const [notifBookings, setNotifBookings] = useState(true);
  const [notifReviews, setNotifReviews] = useState(true);
  const [notifMarketing, setNotifMarketing] = useState(false);

  // Detect plan from user description or default BASIC
  const detectedPlan: string = 'BASIC'; // In production, fetch from profile

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Parolele nu coincid!');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Parola trebuie să aibă minim 8 caractere!');
      return;
    }
    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (res.ok) {
        toast.success('Parola a fost schimbată cu succes!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
      } else {
        const data = await res.json();
        toast.error(data.error || 'Eroare la schimbarea parolei');
      }
    } catch {
      toast.error('Eroare de conexiune. Încearcă din nou.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-3">
            <Settings className="w-7 h-7 text-teal-600" />
            Setări Cont Business
          </h1>
          <p className="text-gray-500 mt-1">Gestionează contul și preferințele centrului tău.</p>
        </div>

        <div className="space-y-6">

          {/* ── Informații Cont ─────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h2 className="font-bold text-gray-800 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-teal-600" />
                Informații Cont
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Email</p>
                    <p className="text-sm font-semibold text-gray-900">{user?.email}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Nume</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center">
                    <BadgeCheck className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Plan activ</p>
                    <div className="mt-0.5">
                      <PlanBadge plan={detectedPlan} />
                    </div>
                  </div>
                </div>
                {!isIOS() && (
                <button onClick={() => navigate('/business')} className="text-xs font-bold text-teal-600 hover:text-teal-700 flex items-center gap-1">Upgrade <ChevronRight className="w-3 h-3" /></button>
                )}
              </div>
            </div>
          </div>

          {/* ── Plan & Abonament ────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h2 className="font-bold text-gray-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-teal-600" />
                Plan & Abonament
              </h2>
            </div>
            <div className="p-6">
              {/* Current plan summary */}
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-bold text-teal-800">Plan {detectedPlan}</p>
                    <p className="text-xs text-teal-600 mt-0.5">
                      {detectedPlan === 'BASIC' && 'Profil + apariție în harta specialiștilor fitness'}
                      {detectedPlan === 'PRO' && 'Tot Basic + programări + postări + statistici'}
                      {detectedPlan === 'PREMIUM' && 'Tot Pro + AI targeting + banner promoțional'}
                    </p>
                  </div>
                  <PlanBadge plan={detectedPlan} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { feature: 'profile', label: 'Profil business' },
                    { feature: 'map', label: 'Hartă specialiști' },
                    { feature: 'bookings', label: 'Programări online' },
                    { feature: 'posts', label: 'Postări & Noutăți' },
                    { feature: 'stats', label: 'Statistici' },
                    { feature: 'ai_targeting', label: 'AI Targeting' },
                  ].map((item) => {
                    const active = (PLAN_FEATURES[detectedPlan] || []).includes(item.feature);
                    return (
                      <div key={item.feature} className={`flex items-center gap-2 text-xs ${active ? 'text-teal-700' : 'text-gray-400'}`}>
                        {active
                          ? <CheckCircle className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                          : <AlertCircle className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                        }
                        {item.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              {detectedPlan !== 'PREMIUM' && !isIOS() && (
                <button
                  onClick={() => navigate('/business')}
                  className="w-full flex items-center justify-center gap-2 bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition"
                >
                  <TrendingUp className="w-4 h-4" />
                  Upgrade Plan
                </button>
              )}

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-xs text-blue-700">
                  💳 Plata abonamentului se face prin transfer bancar sau card. Contactează echipa FII FIT pentru facturare.
                </p>
              </div>
            </div>
          </div>

          {/* ── Securitate ──────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h2 className="font-bold text-gray-800 flex items-center gap-2">
                <Shield className="w-4 h-4 text-teal-600" />
                Securitate
              </h2>
            </div>
            <div className="p-6">
              <button
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
              >
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-gray-500" />
                  <div className="text-left">
                    <p className="font-semibold text-gray-800 text-sm">Schimbă parola</p>
                    <p className="text-xs text-gray-500">Actualizează parola contului tău</p>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showPasswordSection ? 'rotate-90' : ''}`} />
              </button>

              {showPasswordSection && (
                <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
                  {/* Current password */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Parola actuală *
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrent ? 'text' : 'password'}
                        required
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl pr-12 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Parola actuală"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrent(!showCurrent)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* New password */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Parola nouă *
                    </label>
                    <div className="relative">
                      <input
                        type={showNew ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl pr-12 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Minim 8 caractere"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew(!showNew)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {newPassword && newPassword.length < 8 && (
                      <p className="text-xs text-red-500 mt-1">Minim 8 caractere</p>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirmă parola nouă *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full px-4 py-3 border rounded-xl pr-12 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm ${
                          confirmPassword && confirmPassword !== newPassword
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        }`}
                        placeholder="Repetă parola nouă"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {confirmPassword && confirmPassword !== newPassword && (
                      <p className="text-xs text-red-500 mt-1">Parolele nu coincid</p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordSection(false);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition text-sm"
                    >
                      Anulează
                    </button>
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="flex-1 bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition disabled:opacity-50 text-sm"
                    >
                      {passwordLoading ? 'Se salvează...' : 'Salvează parola'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* ── Notificări ──────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h2 className="font-bold text-gray-800 flex items-center gap-2">
                <Bell className="w-4 h-4 text-teal-600" />
                Preferințe Notificări
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {[
                {
                  label: 'Programări noi',
                  desc: 'Primești notificare când un client se programează',
                  value: notifBookings,
                  onChange: setNotifBookings,
                },
                {
                  label: 'Recenzii noi',
                  desc: 'Primești notificare când un client lasă o recenzie',
                  value: notifReviews,
                  onChange: setNotifReviews,
                },
                {
                  label: 'Noutăți FII FIT Business',
                  desc: 'Informații despre funcționalități noi și oferte',
                  value: notifMarketing,
                  onChange: setNotifMarketing,
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2">
                  <div className="flex-1 pr-4">
                    <p className="text-sm font-semibold text-gray-800">{item.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                  </div>
                  <button
                    onClick={() => {
                      item.onChange(!item.value);
                      toast.success(item.value ? 'Notificare dezactivată' : 'Notificare activată');
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      item.value ? 'bg-teal-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        item.value ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ── Profil Business ─────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h2 className="font-bold text-gray-800 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-teal-600" />
                Acțiuni Rapide
              </h2>
            </div>
            <div className="p-4 space-y-2">
              {[
                {
                  label: 'Editează Profilul Centrului',
                  desc: 'Actualizează informații, poze, specialiști',
                  icon: Building2,
                  action: () => navigate('/specialist/profile/edit'),
                  color: 'text-teal-600',
                  bg: 'bg-teal-50',
                },
                {
                  label: 'Dashboard Business',
                  desc: 'Statistici, programări, postări',
                  icon: TrendingUp,
                  action: () => navigate('/specialist/dashboard'),
                  color: 'text-blue-600',
                  bg: 'bg-blue-50',
                },
                ...(isIOS() ? [] : [{
                  label: 'Upgrade Plan',
                  desc: 'Deblochează mai multe funcționalități',
                  icon: CreditCard,
                  action: () => navigate('/business'),
                  color: 'text-indigo-600',
                  bg: 'bg-indigo-50',
                }]),
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition text-left border border-gray-100"
                >
                  <div className={`w-10 h-10 ${item.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 text-sm">{item.label}</p>
                    <p className="text-xs text-gray-500 truncate">{item.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* ── Deconectare ─────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-red-100 shadow-sm overflow-hidden">
            <div className="p-6">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl border-2 border-red-200 text-red-600 font-bold hover:bg-red-50 transition"
              >
                <LogOut className="w-5 h-5" />
                Deconectează-te
              </button>
              <p className="text-center text-xs text-gray-400 mt-3">
                FII FIT Business — Platforma pentru profesioniști fitness
              </p>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
